// PURE tween engine of the board FX (IMPLEMENTATION §2.9 "tweens 200", UX §5 "Tweens 200: settle the oldest
// cosmetic tween instantly"). A tween animates numeric props of a plain record (a view's `anim`, an FX
// sprite record) on the FX clock: its value at clock time t is computed from its start time, so a frozen
// clock (`__gm.render.freezeAt`) freezes every tween exactly and frame timing never changes where it lands.
// A delayed tween applies its `from` values and takes over its props from older tweens only when it
// STARTS (a delayed landing squash never cuts the arc before it). No Phaser tween is used for FX (the spike
// probe still covers Phaser's). Counters back the §3 M10 caps and the reduce-motion spy: `started.scale`
// counts every tween that animates a scale prop, `started.rot` every tilt (helper tweens declare their kind).
import type { EaseFn } from './ease.ts';

export type Props = Record<string, number>;

/** What a tween animates (the reduce-motion spy counts `scale`). */
export type TweenKind = 'scale' | 'move' | 'rot' | 'alpha' | 'other';

export interface TweenSpec {
  target: Props;
  to: Props;
  /** Applied to the target when the tween starts (after its delay). */
  from?: Props;
  ms: number;
  delay?: number;
  ease: EaseFn;
  /** Called once when the tween ends (completed, settled by the cap, overwritten or cleared). */
  onDone?: () => void;
  /** Per-step hook with the eased and the raw progress (arcs, wobbles). */
  onStep?: (k: number, t: number) => void;
  /**
   * What it animates, when `to` does not say (a helper tween that writes a view prop in `onStep`: a wobble
   * is 'rot', an arc 'move'); default from the keys of `to`.
   */
  kind?: TweenKind;
}

const SCALE_KEYS = ['s', 'sx', 'sy', 'scale', 'size'];
const MOVE_KEYS = ['x', 'y', 'dx', 'dy'];

/** The kind of a tween from the props it animates (scale wins: a mixed scale + alpha tween is a scale tween). */
export function tweenKind(to: Props): TweenKind {
  const keys = Object.keys(to);
  if (keys.some((k) => SCALE_KEYS.includes(k))) return 'scale';
  if (keys.some((k) => MOVE_KEYS.includes(k))) return 'move';
  if (keys.includes('rot')) return 'rot';
  if (keys.includes('a')) return 'alpha';
  return 'other';
}

interface Live {
  spec: TweenSpec;
  keys: string[];
  from: Props;
  start: number;
  kind: TweenKind;
  started: boolean;
  done: boolean;
}

export type TweenCounts = Record<TweenKind, number>;

export class Tweens {
  private live: Live[] = [];
  private readonly cap: number;
  private peakN = 0;
  private settledN = 0;
  readonly started: TweenCounts = { scale: 0, move: 0, rot: 0, alpha: 0, other: 0 };

  constructor(cap: number) {
    this.cap = cap;
  }

  /** Starts a tween at clock time `now` (after `delay`); at the cap the oldest tween settles first. */
  add(spec: TweenSpec, now: number): void {
    while (this.live.length >= this.cap) {
      const oldest = this.live[0];
      if (oldest === undefined) break;
      this.finish(oldest, true);
      this.settledN += 1;
      this.compact();
    }
    const kind = spec.kind ?? tweenKind(spec.to);
    this.started[kind] += 1;
    const l: Live = {
      spec,
      keys: Object.keys(spec.to),
      from: {},
      start: now + Math.max(0, spec.delay ?? 0),
      kind,
      started: false,
      done: false,
    };
    this.live.push(l);
    this.peakN = Math.max(this.peakN, this.live.length);
    if (l.start <= now) {
      this.begin(l);
      if (!(spec.ms > 0)) this.finish(l, true);
    }
    this.compact();
  }

  /** A tween starts: its `from` applies, and it takes its props over from older tweens of the record. */
  private begin(l: Live): void {
    l.started = true;
    const target = l.spec.target;
    for (const o of this.live) {
      if (o === l || o.done || !o.started || o.spec.target !== target) continue;
      if (!o.keys.some((k) => l.keys.includes(k))) continue;
      o.keys = o.keys.filter((k) => !l.keys.includes(k));
      if (o.keys.length === 0) this.finish(o, false);
    }
    if (l.spec.from) Object.assign(target, l.spec.from);
    for (const k of l.keys) l.from[k] = target[k] ?? 0;
  }

  /** Applies every tween at clock time `now`; finished ones apply their end values and call onDone. */
  step(now: number): void {
    for (let i = 0; i < this.live.length; i++) {
      const l = this.live[i] as Live;
      if (l.done || now < l.start) continue;
      if (!l.started) this.begin(l);
      const t = l.spec.ms > 0 ? (now - l.start) / l.spec.ms : 1;
      if (t >= 1) {
        this.finish(l, true);
        continue;
      }
      const k = l.spec.ease(t);
      for (const key of l.keys) {
        const a = l.from[key] ?? 0;
        const b = l.spec.to[key] ?? 0;
        l.spec.target[key] = a + (b - a) * k;
      }
      l.spec.onStep?.(k, t);
    }
    this.compact();
  }

  /** Ends every tween with its final values (a rebuild / clear). */
  settleAll(): void {
    for (const l of this.live) {
      if (!l.started && l.spec.from) Object.assign(l.spec.target, l.spec.from);
      this.finish(l, true);
    }
    this.live = [];
  }

  private finish(l: Live, toEnd: boolean): void {
    if (l.done) return;
    l.done = true;
    if (toEnd) {
      for (const key of l.keys) l.spec.target[key] = l.spec.to[key] ?? 0;
      l.spec.onStep?.(1, 1);
    }
    l.spec.onDone?.();
  }

  private compact(): void {
    if (this.live.some((l) => l.done)) this.live = this.live.filter((l) => !l.done);
  }

  /** Tweens running (or waiting for their delay) now. */
  get active(): number {
    return this.live.length;
  }

  /** Most tweens alive at once since the last `resetPeak`. */
  get peak(): number {
    return this.peakN;
  }

  /** Tweens the cap settled early. */
  get settled(): number {
    return this.settledN;
  }

  resetPeak(): void {
    this.peakN = this.live.length;
  }
}
